/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import Model, { Providers } from '@tripian/model';
import classes from './KiwiRoomRateModal.scss';
import { getCurrencySymbol } from '../../currencySymbol';

interface IKiwiRoomRateModal {
  roomTitle: string;
  rate: Providers.Kiwi.KiwiRate;
  currency: string;
  nightCount: number;
  onClose: () => void;
  onBookNow?: () => void;
  t: (value: Model.TranslationKey) => string;
}

const PREMIUM_BENEFITS = [
  'Best available rate guarantee',
  'Automatic room upgrade upon arrival, when available',
  'Complimentary in-room Wi-Fi, when available',
  'Complimentary breakfast for two',
  '$25 USD food or beverage credit',
  'VIP Guest status',
  'Late check-out upon request, when available',
];

const fmt = (val: string | number | undefined, currency: string) => {
  const n = parseFloat(String(val ?? 0)) || 0;
  return `${getCurrencySymbol(currency)} ${new Intl.NumberFormat('en-US').format(Math.round(n))}`;
};

const fmtDate = (dateStr: string) => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
};

const KiwiRoomRateModal: React.FC<IKiwiRoomRateModal> = ({
  roomTitle,
  rate,
  currency,
  nightCount,
  onClose,
  onBookNow,
  t,
}) => {
  const [subtotalOpen, setSubtotalOpen] = useState(false);
  const [taxesOpen, setTaxesOpen] = useState(false);

  const price = rate.price;
  const nightlyPrice = parseFloat(price?.inclusiveNightlyAverage) || 0;
  const subtotal = price?.exclusiveTotal;
  const total = price?.inclusiveTotal;
  const taxesTotal = (parseFloat(price?.totalTaxes || '0') + parseFloat(price?.totalFeesAndSurcharges || '0')).toString();
  const nightlyRates = price?.nightlyRates || [];
  const feeTaxes = price?.feeTaxes || [];
  const nightLabel = nightCount > 0
    ? `${t('kiwi.common.subtotal')} (${nightCount} ${nightCount > 1 ? t('kiwi.common.nights') : t('kiwi.common.night')})`
    : t('kiwi.common.subtotal');

  const titleParts = (rate.title || '').split(' | ');
  const rateTitleMain = titleParts[0] || '';
  const rateTitleSub = titleParts.slice(1).join(' | ');

  const benefits: string[] = rate.specialOfferRate === 'true' ? PREMIUM_BENEFITS : (rate.amenities || []);

  const policies: { label: string; text: string }[] = [];
  if (rate.policies?.deposit) policies.push({ label: t('kiwi.rateModal.policies.deposit'), text: rate.policies.deposit });
  if (rate.policies?.cancellation) policies.push({ label: t('kiwi.rateModal.policies.cancellation'), text: rate.policies.cancellation });
  if (rate.policies?.guarantee) policies.push({ label: t('kiwi.rateModal.policies.guarantee'), text: rate.policies.guarantee });
  if (rate.policies?.tax) policies.push({ label: t('kiwi.rateModal.policies.tax'), text: rate.policies.tax });
  if (rate.policies?.serviceCharges) policies.push({ label: t('kiwi.rateModal.policies.serviceCharges'), text: rate.policies.serviceCharges });
  if (rate.policies?.checkin) policies.push({ label: t('kiwi.rateModal.policies.checkIn'), text: rate.policies.checkin });
  if (rate.policies?.children) policies.push({ label: t('kiwi.rateModal.policies.children'), text: rate.policies.children });
  if (rate.policies?.pets) policies.push({ label: t('kiwi.rateModal.policies.pets'), text: rate.policies.pets });
  if (rate.policies?.prepay) policies.push({ label: t('kiwi.rateModal.policies.prepay'), text: rate.policies.prepay });
  if (rate.policies?.miscellaneous) policies.push({ label: t('kiwi.rateModal.policies.additional'), text: rate.policies.miscellaneous });
  if (rate.policies?.specialRequirements) policies.push({ label: t('kiwi.rateModal.policies.specialRequirements'), text: rate.policies.specialRequirements });

  const content = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={classes.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div className={classes.panel} onClick={(e) => e.stopPropagation()}>

        <button type="button" className={classes.closeBtn} onClick={onClose} aria-label={t('kiwi.common.close')}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 className={classes.roomTitle}>{roomTitle}</h2>

        <div className={classes.body}>
          {/* Left: Price Information */}
          <div className={classes.priceCol}>
            <h3 className={classes.colTitle}>{t('kiwi.rateModal.priceInformation')}</h3>
            <div className={classes.priceCard}>
              {/* Price + Book Now */}
              <div className={classes.priceTopRow}>
                <div className={classes.priceLeft}>
                  <span className={classes.priceAmount}>
                    {getCurrencySymbol(currency)}{new Intl.NumberFormat('en-US').format(Math.round(nightlyPrice))}
                  </span>
                  <span className={classes.priceUnit}>{currency} / {t('kiwi.rateModal.avgNightlyRate')}</span>
                </div>
                {onBookNow && (
                  <button type="button" className={classes.bookNowBtn} onClick={onBookNow}>
                    {t('kiwi.common.bookNow')}
                  </button>
                )}
              </div>

              {/* Details table */}
              <div className={classes.detailsColHeader}>
                <span className={classes.detailsLabel}>{t('kiwi.common.details')}</span>
                <span className={classes.detailsCurrency}>{currency}</span>
              </div>
              <hr className={classes.dividerThick} />

              {/* Subtotal */}
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div className={classes.expandRow} role="button" tabIndex={0} onClick={() => setSubtotalOpen((v) => !v)} onKeyDown={(e) => { if (e.key === 'Enter') setSubtotalOpen((v) => !v); }}>
                <div className={classes.expandLeft}>
                  <span className={classes.expandLabel}>{nightLabel}</span>
                  <svg className={`${classes.expandChevron} ${subtotalOpen ? classes.open : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                <span className={classes.expandAmount}>{fmt(subtotal, currency)}</span>
              </div>
              {subtotalOpen && nightlyRates.length > 0 && (
                <div className={classes.expandedRows}>
                  {nightlyRates.map((nr, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className={classes.expandedRow}>
                      <span className={classes.expandedRowLabel}>{fmtDate(nr.date)}</span>
                      <span className={classes.expandedRowAmount}>{fmt(nr.exclusiveRate, currency)}</span>
                    </div>
                  ))}
                </div>
              )}
              <hr className={classes.divider} />

              {/* Taxes & fees */}
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div className={classes.expandRow} role="button" tabIndex={0} onClick={() => setTaxesOpen((v) => !v)} onKeyDown={(e) => { if (e.key === 'Enter') setTaxesOpen((v) => !v); }}>
                <div className={classes.expandLeft}>
                  <span className={classes.expandLabel}>{t('kiwi.common.hotelTaxesFees')}</span>
                  <svg className={`${classes.expandChevron} ${taxesOpen ? classes.open : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                <span className={classes.expandAmount}>{fmt(taxesTotal, currency)}</span>
              </div>
              {taxesOpen && (
                <div className={classes.expandedRows}>
                  {feeTaxes.length > 0 ? feeTaxes.map((ft, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className={classes.expandedRow}>
                      <span className={classes.expandedRowLabel}>{ft.name || `Fee ${ft.feeTaxCodeId}`}</span>
                      <span className={classes.expandedRowAmount}>{fmt(ft.amount, currency)}</span>
                    </div>
                  )) : (
                    <>
                      {parseFloat(price?.totalFeesAndSurcharges || '0') > 0 && (
                        <div className={classes.expandedRow}>
                          <span className={classes.expandedRowLabel}>{t('kiwi.common.fees')}</span>
                          <span className={classes.expandedRowAmount}>{fmt(price?.totalFeesAndSurcharges, currency)}</span>
                        </div>
                      )}
                      {parseFloat(price?.totalTaxes || '0') > 0 && (
                        <div className={classes.expandedRow}>
                          <span className={classes.expandedRowLabel}>{t('kiwi.common.taxes')}</span>
                          <span className={classes.expandedRowAmount}>{fmt(price?.totalTaxes, currency)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              <hr className={classes.divider} />

              {/* Total */}
              <div className={classes.totalRow}>
                <span className={classes.totalLabel}>{t('kiwi.common.total')}</span>
                <span className={classes.totalAmount}>{fmt(total, currency)}</span>
              </div>

              <p className={classes.priceFooter}>
                {t('kiwi.common.priceDisclaimer')}
              </p>
            </div>
          </div>

          {/* Right: Rate details */}
          <div className={classes.rateCol}>
            <div className={classes.rateTitleRow}>
              <span className={classes.rateTitleMain}>{rateTitleMain}</span>
              {rateTitleSub && (
                <>
                  <span className={classes.rateTitleSep}> | </span>
                  <span className={classes.rateTitleSub}>{rateTitleSub}</span>
                </>
              )}
            </div>

            <div className={classes.rateCard}>
              {/* Premium Benefits */}
              {benefits.length > 0 && (
                <div className={classes.section}>
                  <h4 className={classes.sectionTitle}>{t('kiwi.rateModal.premiumBenefits')}</h4>
                  <ul className={classes.benefitsList}>
                    {benefits.map((b) => (
                      <li key={b} className={classes.benefitItem}>
                        <svg className={classes.checkIcon} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.59L5.41 12l1.42-1.41L10 13.17l7.17-7.17 1.41 1.42L10 16.59z" />
                        </svg>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Restrictions */}
              {rate.description && (
                <div className={classes.section}>
                  <h4 className={classes.sectionTitle}>{t('kiwi.rateModal.restrictions')}</h4>
                  <p className={classes.restrictionsText}>{rate.description}</p>
                </div>
              )}

              {/* Taxes, Fees & Policies */}
              {policies.length > 0 && (
                <div className={classes.section}>
                  <h4 className={classes.sectionTitle}>{t('kiwi.rateModal.taxesFeesPolicies')}</h4>
                  {policies.map((p) => (
                    <div key={p.label} className={classes.policyItem}>
                      <p className={classes.policyTitle}>{p.label}</p>
                      <p className={classes.policyText}>{p.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default KiwiRoomRateModal;
